import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { COARSE_POINTER_HIT_AREA_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import { ColumnResizeHandle } from "./columnResize";
import type { DataTableColumn, DataTableSort } from "./DataTable";

interface DataTableHeaderCellProps {
  readonly column: DataTableColumn;
  readonly width: number;
  readonly sort: DataTableSort | null;
  readonly onSort: (columnId: string) => void;
  readonly onWidthChange: (columnId: string, width: number) => void;
  readonly onResetWidth: (columnId: string) => void;
}

export function DataTableHeaderCell({
  column,
  width,
  sort,
  onSort,
  onWidthChange,
  onResetWidth,
}: DataTableHeaderCellProps) {
  const isActive = sort?.columnId === column.id;

  return (
    <th
      className="relative px-3 py-1.5 text-left"
      aria-sort={columnAriaSort(column, isActive, sort?.direction)}
    >
      <DataTableColumnLabel
        column={column}
        isActive={isActive}
        direction={sort?.direction}
        onSort={onSort}
      />
      {isResizableColumn(column) ? (
        <ColumnResizeHandle
          width={width}
          onWidthChange={(nextWidth) => onWidthChange(column.id, nextWidth)}
          onReset={() => onResetWidth(column.id)}
          ariaLabel={`Resize ${column.header} column`}
        />
      ) : null}
    </th>
  );
}

interface DataTableColumnLabelProps {
  readonly column: DataTableColumn;
  readonly isActive: boolean;
  readonly direction: DataTableSort["direction"] | undefined;
  readonly onSort: (columnId: string) => void;
}

function DataTableColumnLabel({ column, isActive, direction, onSort }: DataTableColumnLabelProps) {
  return column.sortable ? (
    <button
      type="button"
      onClick={() => onSort(column.id)}
      aria-label={`Sort by ${column.header}`}
      className={cn(
        COARSE_POINTER_HIT_AREA_CLASS,
        "flex min-h-touch items-center gap-1 text-left text-[11px] font-medium uppercase tracking-wide transition-colors hover:text-foreground md:min-h-0",
        isActive ? "text-foreground" : "text-muted-foreground",
      )}
    >
      {column.header}
      <DataTableSortIcon isActive={isActive} direction={direction} />
    </button>
  ) : (
    <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {column.header}
    </span>
  );
}

function DataTableSortIcon({
  isActive,
  direction,
}: {
  readonly isActive: boolean;
  readonly direction: DataTableSort["direction"] | undefined;
}) {
  if (!isActive) return null;
  return direction === "asc" ? (
    <ChevronUpIcon className="size-3" />
  ) : (
    <ChevronDownIcon className="size-3" />
  );
}

function columnAriaSort(
  column: DataTableColumn,
  isActive: boolean,
  direction: DataTableSort["direction"] | undefined,
): "ascending" | "descending" | "none" | undefined {
  if (!column.sortable) return undefined;
  if (!isActive) return "none";
  return direction === "asc" ? "ascending" : "descending";
}

function isResizableColumn(column: DataTableColumn): boolean {
  return column.resizable !== false && column.fill !== true;
}
